import { Box, Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import moment from "moment";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  DeadlineSubject,
  useListCombinedEventsForBuyerQuery,
  useListMyUpcomingDeadlinesQuery,
  type UpcomingDeadlineDto,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { urls } from "../../../../urls";
import { URGENCY_COLOR, deadlineUrgency } from "../deadlineUrgency";
import { statusesForBuckets, type BucketKey } from "../../../../common/layout/v2/statusBuckets";
import { useDeadlineLabel } from "../useDeadlineLabel";
import { useHomeLabel } from "../useHomeLabel";
import { PanelBody, PanelCard, PanelHeader } from "./HomePanel";

/**
 * The list opens on the days around today: a handful of what is coming, and the few dates that have
 * just gone by on something still running. Dates from a year ago and dates a year out are both real,
 * but neither is what anyone needs to see every morning — they are a click away instead.
 */
const INITIAL = { upcoming: 5, overdue: 3, withinDays: 90, overdueDays: 30 };
const STEP = { rows: 5, days: 90 };

const PAST_DEADLINE_BUCKETS: BucketKey[] = ["kunngjort", "evaluering"];

export const UpcomingDeadlinesPanel = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const deadlineLabel = useDeadlineLabel();
  const momentLocale = momentLocaleFor(i18n.language);

  const [window, setWindow] = useState(INITIAL);
  const { data, isLoading, isError, isFetching } = useListMyUpcomingDeadlinesQuery({
    upcomingLimit: window.upcoming,
    overdueLimit: window.overdue,
    withinDays: window.withinDays,
    overdueDays: window.overdueDays,
  });
  // Widening the window changes the query arguments, and RTK drops `data` while the new set loads.
  // Holding the last answer keeps the list on screen instead of blanking the card on every click.
  const lastShown = useRef<UpcomingDeadlineDto[]>([]);
  if (data?.deadlines) lastShown.current = data.deadlines;
  const deadlines = data?.deadlines ?? lastShown.current;

  // Each direction grows on its own: asking for more history should not also drag in another
  // quarter of what is coming, and the reverse.
  const showEarlier = () =>
    setWindow((w) => ({ ...w, overdue: w.overdue + STEP.rows, overdueDays: w.overdueDays + STEP.days }));
  const showLater = () =>
    setWindow((w) => ({ ...w, upcoming: w.upcoming + STEP.rows, withinDays: w.withinDays + STEP.days }));
  const overdueShown = deadlines.filter((d) => deadlineUrgency(d.dueAt) === "overdue").length;
  const canShowEarlier = overdueShown >= window.overdue;
  const canShowLater = deadlines.length - overdueShown >= window.upcoming;

  // Composed rather than the one-shot HomePanel: the overdue figure and the widen-the-window
  // buttons stay put when the current window happens to hold nothing.
  return (
    <PanelCard>
      <PanelHeader titleKey="Deadlines" icon={FiClock} />
      <PastDeadlineFigure />
      {canShowEarlier && <WidenButton labelKey="Show earlier" onClick={showEarlier} isLoading={isFetching} />}
      <PanelBody
        isLoading={isLoading && deadlines.length === 0}
        isError={isError}
        isEmpty={deadlines.length === 0}
        emptyKey="No deadlines coming up"
      >
      {/*
        Evenly spaced rather than spaced by time: true intervals would leave weeks of empty rail
        between two dates and crowd three that fall in the same afternoon. The rail carries the order,
        the colour carries the urgency, and the date itself says how far off it is.
      */}
      {/* Capped and scrollable: widening the window twice would otherwise run the card past the
          height of everything beside it. */}
      <Box px="4" py="4" maxH="26rem" overflowY="auto">
        {deadlines.map((deadline, index) => {
          const urgency = deadlineUrgency(deadline.dueAt);
          const isLast = index === deadlines.length - 1;
          return (
            <Flex
              key={`${deadline.subjectId}-${deadline.kind}-${deadline.dueAt}`}
              position="relative"
              pl="6"
              pb={isLast ? "0" : "5"}
              cursor="pointer"
              role="group"
              onClick={() => navigate(deadlineTarget(deadline))}
            >
              {!isLast && (
                <Box position="absolute" left="4px" top="16px" bottom="0" w="2px" bg="smBorder" aria-hidden />
              )}
              <Box
                position="absolute"
                left="0"
                top="6px"
                w="10px"
                h="10px"
                rounded="full"
                bg={URGENCY_COLOR[urgency]}
                aria-hidden
              />
              <Box minW="0" flex="1">
                <Flex align="baseline" gap="2" wrap="wrap">
                  <Text fontSize="sm" color={URGENCY_COLOR[urgency]}>
                    {displayDate(deadline.dueAt)}
                  </Text>
                  <Text fontSize="xs" color={urgency === "overdue" ? "smError" : "smMuted"}>
                    {urgency === "overdue" && `${t("Past the deadline")} · `}
                    {moment(deadline.dueAt).locale(momentLocale).fromNow()}
                  </Text>
                </Flex>
                <Text isTruncated color="smMuted" _groupHover={{ textDecoration: "underline" }}>
                  {deadline.subjectTitle || t("Untitled")}
                </Text>
                <Text isTruncated fontSize="xs" color="smMuted">
                  {deadline.detail
                    ? `${deadlineLabel(deadline.kind)} · ${deadline.detail}`
                    : deadlineLabel(deadline.kind)}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Box>
      </PanelBody>
      {canShowLater && <WidenButton labelKey="Show later" onClick={showLater} isLoading={isFetching} />}
    </PanelCard>
  );
};

/** Widens one side of the window. Bordered like a row so it reads as the end of the list, not a form control. */
const WidenButton = ({
  labelKey,
  onClick,
  isLoading,
}: {
  labelKey: string;
  onClick: () => void;
  isLoading: boolean;
}) => {
  const homeLabel = useHomeLabel();
  return (
    <Button
      variant="ghost"
      size="sm"
      w="full"
      rounded="none"
      color="smMuted"
      fontWeight="normal"
      borderTop="1px solid"
      borderColor="smBorder"
      isLoading={isLoading}
      onClick={onClick}
    >
      {homeLabel(labelKey)}
    </Button>
  );
};

/**
 * How many events are sitting past their deadline — the count the list below can only ever show a
 * few of. Red, because an announced competition nobody closed is not a neutral fact.
 */
const PastDeadlineFigure = () => {
  const navigate = useNavigate();
  const homeLabel = useHomeLabel();
  const authState = useLoggedInWithOrgContextState();

  const { data, isLoading } = useListCombinedEventsForBuyerQuery({
    orgId: authState.selectedOrg.id,
    statuses: statusesForBuckets(PAST_DEADLINE_BUCKETS),
    deadlinePassed: true,
    limit: 1,
  });
  const count = data?.count ?? 0;

  return (
    <Flex
      as="button"
      type="button"
      direction="column"
      align="flex-start"
      w="full"
      px="4"
      py="3"
      borderBottom="1px solid"
      borderColor="smBorder"
      textAlign="left"
      _hover={{ bg: "smBackgroundSecondary" }}
      onClick={() =>
        navigate(`${urls.v2.sourcingEvents.viewAll}?status=${PAST_DEADLINE_BUCKETS.join(",")}&deadline=passed`)
      }
    >
      {isLoading ? (
        <Skeleton h="30px" w="full" my="1" />
      ) : (
        <Text fontSize="2xl" color={count > 0 ? "smError" : "smText"} lineHeight="1.2">
          {count}
        </Text>
      )}
      <Text fontSize="sm" color="smText">
        {homeLabel("Past their deadline")}
      </Text>
      <Text fontSize="xs" color="smMuted" noOfLines={1}>
        {homeLabel("Announced or under evaluation, deadline gone by")}
      </Text>
    </Flex>
  );
};

const deadlineTarget = (deadline: UpcomingDeadlineDto): string => {
  switch (deadline.subject) {
    case DeadlineSubject.SourcingEventV2:
      return urls.v2.sourcingEvents.view.go(deadline.subjectId);
    case DeadlineSubject.SourcingEventV1:
      return urls.events.view.go(deadline.subjectId);
    case DeadlineSubject.Contract:
      return urls.v2.contracts.view.go(deadline.subjectId);
  }
};
